import { Document, Packer, Paragraph, TextRun, AlignmentType, PageBreak } from 'docx';
import { supabase } from '@/integrations/supabase/client';

export const generateMailPackageWord = async (
  dealId: string,
  documentTypes: string[]
): Promise<Blob> => {
  // Fetch deal data
  const { data: dealData, error } = await supabase
    .from('deals')
    .select('*')
    .eq('id', dealId)
    .single();

  if (error || !dealData) {
    throw new Error('Failed to load deal data');
  }

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const sections: Paragraph[] = [];

  // Generate each document type
  for (let i = 0; i < documentTypes.length; i++) {
    const docType = documentTypes[i];
    
    // Add page break between documents (except before first)
    if (i > 0) {
      sections.push(new Paragraph({ children: [new PageBreak()] }));
    }

    switch (docType) {
      case 'coverLetter':
        sections.push(...generateCoverLetter(dealData, currentDate));
        break;
      case 'agreement':
        sections.push(...generateAgreement(dealData, currentDate));
        break;
      case 'verification':
        sections.push(...generateVerification(dealData, currentDate));
        break;
      case 'respa':
        sections.push(...generateRESPA(dealData, currentDate));
        break;
      case 'checklist':
        sections.push(...generateChecklist(dealData, currentDate));
        break;
    }
  }

  const doc = new Document({
    sections: [{
      properties: {},
      children: sections
    }]
  });

  return await Packer.toBlob(doc);
};

const generateCoverLetter = (dealData: any, currentDate: string): Paragraph[] => {
  return [
    new Paragraph({
      children: [
        new TextRun({ text: 'CONTRACT FUNDING GROUP, LLC', bold: true, size: 32 })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: 'Making real estate investments simple', bold: true, size: 24 })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 }
    }),
    new Paragraph({
      children: [new TextRun({ text: currentDate, size: 22 })],
      spacing: { after: 300 }
    }),
    new Paragraph({
      children: [new TextRun({ text: dealData.seller_name, bold: true, size: 22 })],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [new TextRun({ text: dealData.seller_address || '', size: 22 })],
      spacing: { after: 300 }
    }),
    new Paragraph({
      children: [new TextRun({ text: `Dear ${dealData.seller_name},`, size: 22 })],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Thank you for considering Contract Funding Group for the purchase of your land contract at ${dealData.property_address}. We are pleased to present our offer for your review.`,
          size: 22
        })
      ],
      spacing: { after: 300 }
    }),
    new Paragraph({
      children: [new TextRun({ text: 'Offer Summary:', bold: true, size: 22 })],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: `Property: ${dealData.property_address}`, size: 22 })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Offer Price: $${dealData.offer_price?.toLocaleString() || '0'}`,
          size: 22
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Current Balance: $${dealData.current_balance?.toLocaleString() || '0'}`,
          size: 22
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Monthly Payment: $${dealData.payment_amount?.toLocaleString() || '0'}`,
          size: 22
        })
      ],
      spacing: { after: 300 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: 'Enclosed you will find the necessary documents for your review and signature. Please review all documents carefully and return them at your earliest convenience.',
          size: 22
        })
      ],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [new TextRun({ text: 'We look forward to working with you.', size: 22 })],
      spacing: { after: 400 }
    }),
    new Paragraph({
      children: [new TextRun({ text: 'Sincerely,', size: 22 })],
      spacing: { after: 600 }
    }),
    new Paragraph({
      children: [new TextRun({ text: 'Contract Funding Group Team', bold: true, size: 22 })],
      spacing: { after: 200 }
    })
  ];
};

const generateAgreement = (dealData: any, currentDate: string): Paragraph[] => {
  return [
    new Paragraph({
      children: [
        new TextRun({ text: 'PURCHASE AGREEMENT', bold: true, size: 28 })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `For Land Contract at ${dealData.property_address}`,
          size: 20
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 }
    }),
    new Paragraph({
      children: [new TextRun({ text: `Date: ${currentDate}`, size: 22 })],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [new TextRun({ text: `Seller: ${dealData.seller_name}`, size: 22 })],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [new TextRun({ text: `Buyer: ${dealData.buyer_name}`, size: 22 })],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [new TextRun({ text: `Property: ${dealData.property_address}`, size: 22 })],
      spacing: { after: 300 }
    }),
    new Paragraph({
      children: [new TextRun({ text: 'TERMS:', bold: true, size: 24 })],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Purchase Price: $${dealData.offer_price?.toLocaleString() || '0'}`,
          size: 22
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Original Sales Price: $${dealData.sales_price?.toLocaleString() || '0'}`,
          size: 22
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Current Balance: $${dealData.current_balance?.toLocaleString() || '0'}`,
          size: 22
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Interest Rate: ${dealData.interest_rate || '0'}%`,
          size: 22
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Monthly Payment: $${dealData.payment_amount?.toLocaleString() || '0'}`,
          size: 22
        })
      ],
      spacing: { after: 400 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: '[Full agreement terms and conditions would appear here in the actual document]',
          size: 20,
          italics: true
        })
      ],
      spacing: { after: 600 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: '_________________________________', size: 22 })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [new TextRun({ text: 'Seller Signature', size: 22 })],
      spacing: { after: 400 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: '_________________________________', size: 22 })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [new TextRun({ text: 'Contract Funding Group, LLC', size: 22 })],
      spacing: { after: 200 }
    })
  ];
};

const generateVerification = (dealData: any, currentDate: string): Paragraph[] => {
  return [
    new Paragraph({
      children: [
        new TextRun({ text: 'CONTRACT VERIFICATION FORM', bold: true, size: 28 })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 }
    }),
    new Paragraph({
      children: [new TextRun({ text: `Buyer Name: ${dealData.buyer_name}`, size: 22 })],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: `Buyer Address: ${dealData.buyer_address || ''}`, size: 22 })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: `Property Address: ${dealData.property_address}`, size: 22 })
      ],
      spacing: { after: 300 }
    }),
    new Paragraph({
      children: [new TextRun({ text: 'Please verify the following:', bold: true, size: 22 })],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: '☐ I am the current buyer on this land contract', size: 22 })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: '☐ All payment information is accurate', size: 22 })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: '☐ I have possession of the original contract', size: 22 })
      ],
      spacing: { after: 400 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: 'Signature: _________________________________', size: 22 })
      ],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: 'Date: _________________________________', size: 22 })
      ],
      spacing: { after: 200 }
    })
  ];
};

const generateRESPA = (dealData: any, currentDate: string): Paragraph[] => {
  const currentYear = new Date().getFullYear();
  
  return [
    new Paragraph({
      children: [
        new TextRun({ text: 'RESPA COMPLIANCE CERTIFICATION', bold: true, size: 28 })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 }
    }),
    new Paragraph({
      children: [new TextRun({ text: `Year: ${currentYear}`, size: 22 })],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: `Property: ${dealData.property_address}`, size: 22 })
      ],
      spacing: { after: 300 }
    }),
    new Paragraph({
      children: [new TextRun({ text: 'Seller Certification:', bold: true, size: 22 })],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [new TextRun({ text: '☐ I did not originate this loan', size: 22 })],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: '☐ I have originated ____ notes this year', size: 22 })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: '☐ Of those, ____ were at high interest rates (>8%)',
          size: 22
        })
      ],
      spacing: { after: 400 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: 'I certify that the information provided above is true and accurate to the best of my knowledge.',
          size: 22
        })
      ],
      spacing: { after: 400 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: 'Signature: _________________________________', size: 22 })
      ],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: 'Date: _________________________________', size: 22 })
      ],
      spacing: { after: 200 }
    })
  ];
};

const generateChecklist = (dealData: any, currentDate: string): Paragraph[] => {
  return [
    new Paragraph({
      children: [
        new TextRun({ text: 'CONTRACT FUNDING GROUP, LLC', bold: true, size: 32 })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: 'Making real estate investments simple', bold: true, size: 24 })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 }
    }),
    new Paragraph({
      children: [new TextRun({ text: currentDate, size: 22 })],
      spacing: { after: 300 }
    }),
    new Paragraph({
      children: [new TextRun({ text: dealData.seller_name, bold: true, size: 22 })],
      spacing: { after: 300 }
    }),
    new Paragraph({
      children: [new TextRun({ text: `Dear ${dealData.seller_name},`, size: 22 })],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: 'Below is a checklist of items needed for your cash offer underwriting:',
          size: 22
        })
      ],
      spacing: { after: 300 }
    }),
    new Paragraph({
      children: [new TextRun({ text: 'Required Documentation:', bold: true, size: 22 })],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [new TextRun({ text: '☐ Signed Purchase Agreement', size: 22 })],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: '☐ Proof of last 6 months of payments from buyer',
          size: 22
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [new TextRun({ text: '☐ Copy of original land contract', size: 22 })],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: '☐ Completed Contract Verification Form', size: 22 })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: '☐ Completed RESPA Compliance Form', size: 22 })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [new TextRun({ text: '☐ Current property tax statement', size: 22 })],
      spacing: { after: 300 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: 'Please review this checklist carefully and gather all required documents.',
          size: 22
        })
      ],
      spacing: { after: 400 }
    }),
    new Paragraph({
      children: [new TextRun({ text: 'Sincerely,', size: 22 })],
      spacing: { after: 600 }
    }),
    new Paragraph({
      children: [new TextRun({ text: 'Contract Funding Group Team', bold: true, size: 22 })],
      spacing: { after: 200 }
    })
  ];
};
